'use client';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Link } from '@/i18n/navigation';
import { Popcorn, Zap } from 'lucide-react';
import { useTranslations } from 'next-intl';
import React from 'react';
import { AuthButtons } from './AuthButtons';

interface MobileMenuProps {
  isMenuOpen: boolean;
  closeMenu: () => void;
  isAuthenticated: boolean;
  isSigningOut: boolean;
  user: any;
  handleLogout: () => void;
  isAdmin: boolean;
  setIsAuthModalOpen: (isOpen: boolean) => void;
  linkTarget?: string;
  balance?: number | null;
  periodTotal?: number | null;
}

export const MobileMenu = ({
  isMenuOpen,
  closeMenu,
  isAuthenticated,
  isSigningOut,
  user,
  handleLogout,
  isAdmin,
  setIsAuthModalOpen,
  linkTarget,
  balance,
  periodTotal,
}: MobileMenuProps) => {
  const t = useTranslations('userMenu');
  const baseId = React.useId();
  const creditsBadgeRef = React.useRef<HTMLSpanElement>(null);
  const [badgeWidth, setBadgeWidth] = React.useState<number | null>(null);
  const [openItem, setOpenItem] = React.useState<string | undefined>(undefined);

  return (
    <ul
      className={`nav-menu force-light ${isMenuOpen ? 'active' : ''} [&_li]:!mb-0 [&_h3]:!mb-0 flex flex-col items-start justify-start border-none bg-white py-0 shadow-[0_8px_30px_rgba(0,0,0,0.2)] md:hidden`}
    >
      <li className="!mb-0 w-full">
        <Accordion
          id={baseId}
          type="single"
          collapsible
          className="w-full"
          value={openItem}
          onValueChange={setOpenItem}
        >
          <AccordionItem value="automate" className="border-b-0">
            <AccordionTrigger aria-label="Automate menu" className="flex h-16 cursor-pointer items-center justify-between border-gray-200 border-b px-4 py-0 text-left font-bold text-lg hover:bg-gray-50 hover:no-underline data-[state=open]:border-gray-200">
              Automate
            </AccordionTrigger>
            <AccordionContent className="bg-gray-200 p-0">
              <ul className="-ml-4 flex w-[calc(100%+2rem)] flex-col gap-4 border-gray-200 border-l-4 bg-gray-200 py-4 pl-16 text-left">
                <li>
                  <Link
                    href="/#automate"
                    target={linkTarget}
                    className="group -ml-2 block w-full cursor-pointer rounded-md p-2 transition-colors hover:bg-white"
                    onClick={closeMenu}
                  >
                    <div className="!text-black font-bold text-base group-hover:text-primary">
                      ZapFlow
                    </div>
                    <div className="text-muted-foreground text-sm">Business Process Automation</div>
                  </Link>
                </li>
                <li>
                  <Link
                    href="/calculator"
                    target={linkTarget}
                    className="group -ml-2 block w-full cursor-pointer rounded-md p-2 transition-colors hover:bg-white"
                    onClick={closeMenu}
                  >
                    <div className="!text-black font-bold text-base group-hover:text-primary">
                      ROI Calculator
                    </div>
                    <div className="text-muted-foreground text-sm">Calculate your savings</div>
                  </Link>
                </li>
                <li>
                  <Link
                    href="/#getstarted"
                    target={linkTarget}
                    className="group -ml-2 block w-full cursor-pointer rounded-md p-2 transition-colors hover:bg-white"
                    onClick={closeMenu}
                  >
                    <div className="!text-black font-bold text-base group-hover:text-primary">
                      Screen Recording
                    </div>
                    <div className="text-muted-foreground text-sm">Record your screen</div>
                  </Link>
                </li>
                <li>
                  <Link
                    href="/#contact"
                    target={linkTarget}
                    className="group -ml-2 block w-full cursor-pointer rounded-md p-2 transition-colors hover:bg-white"
                    onClick={closeMenu}
                  >
                    <div className="!text-black font-bold text-base group-hover:text-primary">
                      Book a Demo
                    </div>
                    <div className="text-muted-foreground text-sm">Schedule a demo</div>
                  </Link>
                </li>
              </ul>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="create" className="border-b-0">
            <AccordionTrigger aria-label="Create menu" className="flex h-16 cursor-pointer items-center justify-between border-gray-200 border-b px-4 py-0 text-left font-bold text-lg hover:bg-gray-50 hover:no-underline data-[state=open]:border-gray-200">
              Create
            </AccordionTrigger>
            <AccordionContent className="bg-gray-200 p-0">
              <ul className="-ml-4 flex w-[calc(100%+2rem)] flex-col gap-4 border-gray-200 border-l-4 bg-gray-200 py-4 pl-16 text-left">
                {/* VIDEOS Section */}
                <li className="-ml-2 py-1">
                  <span className="!text-gray-400 font-bold text-xs uppercase tracking-wider">
                    VIDEO GENERATION
                  </span>
                </li>
                <li>
                  <Link
                    href="/grok-imagine-ai-video-generator"
                    target={linkTarget}
                    className="group -ml-2 block w-full cursor-pointer rounded-md p-2 transition-colors hover:bg-white"
                    onClick={closeMenu}
                  >
                    <div className="!text-black font-bold text-base group-hover:text-primary">
                      Grok Imagine
                    </div>
                    <div className="text-muted-foreground text-sm">Elite AI video generation</div>
                  </Link>
                </li>
                <li>
                  <Link
                    href="/grok-imagine-ai-video-generator#explore"
                    target={linkTarget}
                    className="group -ml-2 block w-full cursor-pointer rounded-md p-2 transition-colors hover:bg-white"
                    onClick={closeMenu}
                  >
                    <div className="!text-black font-bold text-base group-hover:text-primary">
                      Explore Grok Imagine
                    </div>
                    <div className="text-muted-foreground text-sm">Browse Grok videos</div>
                  </Link>
                </li>
                <li>
                  <Link
                    href="/runway-gen-4-ai-video-generator"
                    target={linkTarget}
                    className="group -ml-2 block w-full cursor-pointer rounded-md p-2 transition-colors hover:bg-white"
                    onClick={closeMenu}
                  >
                    <div className="!text-black font-bold text-base group-hover:text-primary">
                      Runway Gen-4
                    </div>
                    <div className="text-muted-foreground text-sm">Cinema-quality AI video</div>
                  </Link>
                </li>
                <li>
                  <Link
                    href="/runway-gen-4-ai-video-generator#explore"
                    target={linkTarget}
                    className="group -ml-2 block w-full cursor-pointer rounded-md p-2 transition-colors hover:bg-white"
                    onClick={closeMenu}
                  >
                    <div className="!text-black font-bold text-base group-hover:text-primary">
                      Explore Runway Gen-4
                    </div>
                    <div className="text-muted-foreground text-sm">Professional video gallery</div>
                  </Link>
                </li>

                {/* IMAGES Section */}
                <li className="-ml-2 py-1">
                  <span className="!text-gray-400 font-bold text-xs uppercase tracking-wider">
                    IMAGE GENERATION
                  </span>
                </li>
                <li>
                  <Link
                    href="/nano-banana-2-ai-image-generator"
                    target={linkTarget}
                    className="group -ml-2 block w-full cursor-pointer rounded-md p-2 transition-colors hover:bg-white"
                    onClick={closeMenu}
                  >
                    <div className="!text-black font-bold text-base group-hover:text-primary">
                      Nano Banana 2
                    </div>
                    <div className="text-muted-foreground text-sm">
                      Google&apos;s latest image model
                    </div>
                  </Link>
                </li>
                <li>
                  <Link
                    href="/nano-banana-2-ai-image-generator#explore"
                    target={linkTarget}
                    className="group -ml-2 block w-full cursor-pointer rounded-md p-2 transition-colors hover:bg-white"
                    onClick={closeMenu}
                  >
                    <div className="!text-black font-bold text-base group-hover:text-primary">
                      Explore Nano Banana 2
                    </div>
                    <div className="text-muted-foreground text-sm">Discover new generations</div>
                  </Link>
                </li>
                <li>
                  <Link
                    href="/z-image-ai-image-generator"
                    target={linkTarget}
                    className="group -ml-2 block w-full cursor-pointer rounded-md p-2 transition-colors hover:bg-white"
                    onClick={closeMenu}
                  >
                    <div className="!text-black font-bold text-base group-hover:text-primary">
                      Z-Image
                    </div>
                    <div className="text-muted-foreground text-sm">High-quality AI images</div>
                  </Link>
                </li>
                <li>
                  <Link
                    href="/z-image-ai-image-generator#explore"
                    target={linkTarget}
                    className="group -ml-2 block w-full cursor-pointer rounded-md p-2 transition-colors hover:bg-white"
                    onClick={closeMenu}
                  >
                    <div className="!text-black font-bold text-base group-hover:text-primary">
                      Explore Z-Image
                    </div>
                    <div className="text-muted-foreground text-sm">Browse community images</div>
                  </Link>
                </li>

                {/* BOTTOM Section */}
                <div className="my-1 h-px bg-gray-300" />
                <li>
                  <Link
                    href="/pricing"
                    target={linkTarget}
                    className="group -ml-2 block w-full cursor-pointer rounded-md p-2 transition-colors hover:bg-white"
                    onClick={closeMenu}
                  >
                    <div className="!text-black font-bold text-base group-hover:text-primary">
                      Pricing
                    </div>
                    <div className="text-muted-foreground text-sm">View pricing plans</div>
                  </Link>
                </li>
              </ul>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="communicate" className="border-b-0">
            <AccordionTrigger aria-label="Communicate menu" className="flex h-16 cursor-pointer items-center justify-between border-gray-200 border-b px-4 py-0 text-left font-bold text-lg hover:bg-gray-50 hover:no-underline data-[state=open]:border-gray-200">
              Communicate
            </AccordionTrigger>
            <AccordionContent className="bg-gray-200 p-0">
              <ul className="-ml-4 flex w-[calc(100%+2rem)] flex-col gap-4 border-gray-200 border-l-4 bg-gray-200 py-4 pl-16 text-left">
                <li>
                  <Link
                    href="/#communicate"
                    target={linkTarget}
                    className="group -ml-2 block w-full cursor-pointer rounded-md p-2 transition-colors hover:bg-white"
                    onClick={closeMenu}
                  >
                    <div className="!text-black font-bold text-base group-hover:text-primary">
                      ZapChat
                    </div>
                    <div className="text-muted-foreground text-sm">Smart AI Chatbots</div>
                  </Link>
                </li>
                <li>
                  <Link
                    href="/#communicate"
                    target={linkTarget}
                    className="group -ml-2 block w-full cursor-pointer rounded-md p-2 transition-colors hover:bg-white"
                    onClick={closeMenu}
                  >
                    <div className="!text-black font-bold text-base group-hover:text-primary">
                      ZapVoice
                    </div>
                    <div className="text-muted-foreground text-sm">AI Voice Agents</div>
                  </Link>
                </li>
                <li>
                  <Link
                    href="/#communicate"
                    target={linkTarget}
                    className="group -ml-2 block w-full cursor-pointer rounded-md p-2 transition-colors hover:bg-white"
                    onClick={closeMenu}
                  >
                    <div className="!text-black font-bold text-base group-hover:text-primary">
                      ZapVideo
                    </div>
                    <div className="text-muted-foreground text-sm">Automated Video Publishing</div>
                  </Link>
                </li>
              </ul>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </li>

      {/* Credits & Gallery - always visible when authenticated */}
      {isAuthenticated && !isSigningOut && (
        <>
          <li className="!mb-0 w-full min-[380px]:hidden">
            <Link
              href="/user/credits"
              target={linkTarget}
              className="group flex h-16 w-full cursor-pointer items-center gap-3 border-gray-200 border-b px-4 py-0 font-bold text-lg transition-colors hover:bg-gray-50"
              onClick={closeMenu}
            >
              {balance === null && periodTotal === null ? (
                <div className="shimmer-base h-6 w-28 rounded-full" />
              ) : (
                <>
                  <Zap className="!text-black h-4 w-4 fill-current group-hover:text-primary" />
                  <span className="!text-black group-hover:text-primary">
                    {balance === 0 ? '350+' : `${balance?.toLocaleString()} Credits`}
                  </span>
                </>
              )}
            </Link>
          </li>
          <li className="!mb-0 w-full">
            <Link
              href="/user/gallery"
              target={linkTarget}
              className="group flex h-16 w-full cursor-pointer items-center gap-3 border-gray-200 border-b px-4 py-0 font-bold text-lg transition-colors hover:bg-gray-50"
              onClick={closeMenu}
            >
              <div className="flex h-5 w-4 items-center justify-center">
                <Popcorn className="!text-black h-5 w-5 transition-colors group-hover:text-primary" />
              </div>
              <span className="!text-black group-hover:text-primary">Gallery</span>
            </Link>
          </li>
        </>
      )}

      <li className="!mb-0 w-full">
        <Accordion
          id={`${baseId}-user`}
          type="single"
          collapsible
          className="w-full"
          value={openItem}
          onValueChange={setOpenItem}
        >
          {isAuthenticated && !isSigningOut && (
            <AccordionItem value="user-menu" className="border-b-0">
              <AccordionTrigger aria-label="User menu" className="flex h-16 cursor-pointer items-center justify-between border-gray-200 border-b px-4 py-0 text-left font-bold text-lg hover:bg-gray-50 hover:no-underline data-[state=open]:border-gray-200 [&>svg]:translate-y-0">
                <div className="flex items-center gap-3">
                  <Avatar className="h-[32px] w-[32px]">
                    <AvatarImage
                      src={user?.image || undefined}
                      alt={
                        user?.name || user?.lastName
                          ? `${user?.name || ''} ${user?.lastName || ''}`.trim()
                          : user?.email || 'User'
                      }
                    />
                    <AvatarFallback className="font-normal text-sm">
                      {user?.name && user?.lastName
                        ? `${user.name[0]}${user.lastName[0]}`.toUpperCase()
                        : (user?.name?.[0]?.toUpperCase() ??
                          user?.email?.[0]?.toUpperCase() ??
                          'U')}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col text-left">
                    <span className="font-bold text-sm">
                      {user?.name || user?.lastName
                        ? `${user.name || ''} ${user.lastName || ''}`.trim()
                        : user?.email || t('user')}
                    </span>
                    <span className="!text-zinc-500 font-normal text-xs">{user?.email}</span>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="bg-gray-200 p-0">
                <ul className="-ml-4 flex w-[calc(100%+2rem)] flex-col gap-2 border-gray-200 border-l-4 bg-gray-200 py-4 pl-16 text-left">
                  <li className="w-full">
                    <Link
                      href="/user/profile"
                      className="group -ml-2 block w-full cursor-pointer rounded-md p-2 transition-colors hover:bg-white"
                      onClick={closeMenu}
                      target={linkTarget}
                    >
                      <div className="!text-black font-bold text-base group-hover:text-primary">
                        {t('profile')}
                      </div>
                      <div className="!text-zinc-500 text-sm">Manage your personal details</div>
                    </Link>
                  </li>
                  <li className="w-full">
                    <Link
                      href="/user/billing"
                      className="group -ml-2 block w-full cursor-pointer rounded-md p-2 transition-colors hover:bg-white"
                      onClick={closeMenu}
                      target={linkTarget}
                    >
                      <div className="!text-black font-bold text-base group-hover:text-primary">
                        {t('billing')}
                      </div>
                      <div className="text-muted-foreground text-sm">View history and plans</div>
                    </Link>
                  </li>
                  <li className="w-full">
                    <Link
                      href="/user/security"
                      className="group -ml-2 block w-full cursor-pointer rounded-md p-2 transition-colors hover:bg-white"
                      onClick={closeMenu}
                      target={linkTarget}
                    >
                      <div className="!text-black font-bold text-base group-hover:text-primary">
                        {t('security')}
                      </div>
                      <div className="!text-zinc-500 text-sm">Update password and settings</div>
                    </Link>
                  </li>

                  {/* Help & Support Section */}
                  <div className="my-1 h-px bg-gray-200" />
                  <li className="-ml-2 py-1">
                    <span className="!text-gray-400 font-bold text-xs uppercase tracking-wider">
                      Help & Support
                    </span>
                  </li>
                  <li className="w-full">
                    <Link
                      href="/pricing"
                      className="group -ml-2 block w-full cursor-pointer rounded-md p-2 transition-colors hover:bg-white"
                      onClick={closeMenu}
                      target={linkTarget}
                    >
                      <div className="!text-black font-bold text-base group-hover:text-primary">
                        See Plans & Pricing
                      </div>
                      <div className="text-muted-foreground text-sm">View pricing plans</div>
                    </Link>
                  </li>
                  <li className="w-full">
                    <Link
                      href="/how-it-works"
                      className="group -ml-2 block w-full cursor-pointer rounded-md p-2 transition-colors hover:bg-white"
                      onClick={closeMenu}
                      target={linkTarget}
                    >
                      <div className="!text-black font-bold text-base group-hover:text-primary">
                        How it works
                      </div>
                      <div className="text-muted-foreground text-sm">Learn the process</div>
                    </Link>
                  </li>
                  <li className="w-full">
                    <Link
                      href="/help"
                      className="group -ml-2 block w-full cursor-pointer rounded-md p-2 transition-colors hover:bg-white"
                      onClick={closeMenu}
                      target={linkTarget}
                    >
                      <div className="!text-black font-bold text-base group-hover:text-primary">
                        FAQs & Help
                      </div>
                      <div className="text-muted-foreground text-sm">Get answers</div>
                    </Link>
                  </li>
                  <li className="w-full">
                    <Link
                      href="/contact"
                      className="group -ml-2 block w-full cursor-pointer rounded-md p-2 transition-colors hover:bg-white"
                      onClick={closeMenu}
                      target={linkTarget}
                    >
                      <div className="!text-black font-bold text-base group-hover:text-primary">
                        Contact Us
                      </div>
                      <div className="text-muted-foreground text-sm">Get in touch</div>
                    </Link>
                  </li>
                  <div className="my-1 h-px bg-gray-200" />

                  <li className="w-full">
                    <button
                      onClick={handleLogout}
                      className="group -ml-2 block w-full cursor-pointer rounded-md p-2 text-left transition-colors hover:bg-white"
                    >
                      <div className="!text-black font-bold text-base group-hover:text-primary">
                        {t('logout')}
                      </div>
                      <div className="text-muted-foreground text-sm">Sign out of your account</div>
                    </button>
                  </li>

                  {isAdmin && (
                    <>
                      <div className="my-1 h-px bg-gray-100" />
                      <li className="-ml-2 py-1">
                        <span className="!text-gray-400 font-bold text-xs uppercase tracking-wider">
                          Admin
                        </span>
                      </li>
                      <li className="w-full">
                        <Link
                          href="/admin/marketing"
                          className="group -ml-2 block w-full cursor-pointer rounded-md p-2 transition-colors hover:bg-white"
                          onClick={closeMenu}
                          target={linkTarget}
                        >
                          <div className="!text-black font-bold text-base group-hover:text-primary">
                            Marketing
                          </div>
                          <div className="text-muted-foreground text-sm">View marketing stats</div>
                        </Link>
                      </li>
                      <li className="w-full">
                        <Link
                          href="/admin/services"
                          className="group -ml-2 block w-full cursor-pointer rounded-md p-2 transition-colors hover:bg-white"
                          onClick={closeMenu}
                          target={linkTarget}
                        >
                          <div className="!text-black font-bold text-base group-hover:text-primary">
                            Services
                          </div>
                          <div className="!text-zinc-500 text-sm">Manage system services</div>
                        </Link>
                      </li>
                      <li className="w-full">
                        <Link
                          href="/admin/users"
                          className="group -ml-2 block w-full cursor-pointer rounded-md p-2 transition-colors hover:bg-white"
                          onClick={closeMenu}
                          target={linkTarget}
                        >
                          <div className="!text-black font-bold text-base group-hover:text-primary">
                            Users
                          </div>
                          <div className="!text-zinc-500 text-sm">Manage registered users</div>
                        </Link>
                      </li>
                    </>
                  )}
                </ul>
              </AccordionContent>
            </AccordionItem>
          )}
        </Accordion>
      </li>

      <AuthButtons
        isMobile={true}
        isAuthenticated={isAuthenticated}
        setIsAuthModalOpen={setIsAuthModalOpen}
        closeMenu={closeMenu}
        linkTarget={linkTarget}
        openItem={openItem}
        onOpenItemChange={setOpenItem}
      />
    </ul>
  );
};
